import { useEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { playSound, playSoundLoop, Sound } from '~/assets/sounds';
import { Text } from '~/components/ui/Text';
import { rage, useObservable } from '~/rage';
import { formatTime } from '~/shared';
import { AppConnectionSwitchGameField } from '../AppConnectionSwitchGameField';
import { ResultOverlay } from '../ResultOverlay';
import {
	StyledContainer,
	StyledTimer,
	StyledWrapper,
} from './AppConnectionSwitchGame.styled';

const AppConnectionSwitchGame = () => {
	const gameTime = useObservable(rage.jobElectrician.getGameTimeSwitch());
	const [timeLeft, setTimeLeft] = useState(gameTime);
	const [gameResultReported, setGameResultReported] = useState(false);
	const [gameResult, setGameResult] = useState<'win' | 'lose'>('lose');
	const [gameEnded, setGameEnded] = useState(false);
	const startTimeRef = useRef(Date.now());
	const gameResultRef = useRef<'win' | 'lose' | null>(null);
	const stopSound = useMemo(() => playSoundLoop(Sound.ElectricMusic), []);

	const circuitNumber = useMemo(
		() => Math.floor(Math.random() * 900) + 100,
		[],
	);

	const timeString = useMemo(
		() => formatTime(Math.max(timeLeft, 0), '{mm}:{ss}'),
		[timeLeft],
	);

	const handleGameSolved = () => {
		if (gameResultRef.current) return;

		gameResultRef.current = 'win';
		setGameResult('win');

		setTimeout(() => {
			setGameEnded(true);
		}, 1500);

		playSound(Sound.ElectricFinish);
		stopSound();

		setTimeout(() => {
			rage.jobElectrician
				.reportWin()
				.catch((err) => rage.notifications.showException(err));
			setGameResultReported(true);
		}, 1500);

		setTimeout(() => {
			rage.notifications.showSuccess('Вы восстановили связь');
		}, 1000);
	};

	useEffect(() => {
		if (timeLeft <= 0) {
			if (!gameResultReported && !gameEnded && !gameResultRef.current) {
				gameResultRef.current = 'lose';
				rage.notifications.showError('Вы не успели восстановить связь!');
				setGameResult('lose');
				setGameEnded(true);
				stopSound();
				playSound(Sound.ElectricLoss);
				setTimeout(() => {
					rage.jobElectrician
						.reportLoss()
						.catch((err) => rage.notifications.showException(err));
					setGameResultReported(true);
				}, 1500);
			}
			return;
		}

		const updateTime = () => {
			const elapsed = Date.now() - startTimeRef.current;
			const remaining = Math.max(gameTime - elapsed, 0);
			setTimeLeft(remaining);

			if (remaining > 0) {
				setTimeout(updateTime, 1000);
			}
		};

		const timeout = setTimeout(updateTime, 1000);

		return () => clearTimeout(timeout);
	}, [gameTime, gameResultReported, gameEnded, timeLeft, stopSound]);

	return (
		<StyledWrapper>
			<StyledContainer>
				<Text
					style={{
						fontSize: 20,
						fontWeight: 500,
						color: '#ffffff',
						marginTop: 35,
					}}
				>
					Разрыв связи №{circuitNumber}
				</Text>
				<StyledTimer>
					<Text>{timeString}</Text>
				</StyledTimer>
				<AppConnectionSwitchGameField handleGameSolved={handleGameSolved} />
				<Text
					style={{
						fontSize: 13,
						color: '#ffffff',
						opacity: 0.5,
						marginTop: 484,
					}}
				>
					Восстановите соединения до истечения времени
				</Text>
			</StyledContainer>
			<CSSTransition
				in={gameEnded}
				timeout={300}
				classNames="overlay"
				unmountOnExit
			>
				<ResultOverlay type={gameResult} />
			</CSSTransition>
		</StyledWrapper>
	);
};

export { AppConnectionSwitchGame as AppConnectionSwitchGame };
